/**
 * AIChat.js
 * -----------------------------------------------------------------------------
 * The UCMA AI assistant chat window. Rendered inside the main chat content pane
 * (same two-column layout as normal chats) so the contacts sidebar stays visible.
 *
 * Features:
 *  - Streaming responses (ChatGPT-style gradual text) with a "Thinking..." state
 *  - Markdown / code / tables / lists / links rendering + copy-code button
 *  - Conversation memory (persisted server-side), New Chat, Clear Memory, Export
 *  - Suggested prompt cards on an empty conversation
 *  - Message actions: Copy, Reply, Star, Delete for me, Share
 *  - Search messages, auto-scroll, timestamps, message status
 *  - Input: text, emoji, voice (speech-to-text), file / image / paste / drag&drop
 *  - Settings: provider/model, response length, creativity, language, dark mode
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Markdown from './Markdown';
import * as aiApi from './aiApi';
import './AIChat.css';

const SUGGESTED_PROMPTS = [
  { icon: '❓', title: 'App Help', text: 'How do I create a group chat in Ucmaapp?' },
  { icon: '📞', title: 'Calls Help', text: 'How do I make voice or video calls in Ucmaapp?' },
  { icon: '💡', title: 'Explain this', text: 'Explain the theory of relativity in simple terms.' },
  { icon: '🌐', title: 'Translate', text: 'Translate "Good morning, have a great day!" into French, Spanish and Hindi.' },
  { icon: '📝', title: 'Summarize', text: 'Summarize the key benefits of regular exercise in 5 points.' },
  { icon: '✉️', title: 'Write Email', text: 'Write a professional email requesting a meeting next week.' },
  { icon: '✈️', title: 'Plan a Trip', text: 'Plan a 3-day budget trip itinerary to Goa.' },
  { icon: '🍎', title: 'Health Tip', text: 'Give me simple tips to stay healthy and energetic at work.' },
  { icon: '🎓', title: 'Study Help', text: 'Explain photosynthesis in a way a 10-year-old can understand.' },
  { icon: '💼', title: 'Business Idea', text: 'Suggest 5 small business ideas I can start with low investment.' },
  { icon: '🧭', title: 'Career Advice', text: 'How do I prepare for a job interview? Give me the key tips.' },
  { icon: '✍️', title: 'Writing Help', text: 'Help me write a short heartfelt birthday wish for my best friend.' },
];

const DEFAULT_SETTINGS = {
  provider: '',
  model: '',
  temperature: 0.7,
  responseLength: 'medium',
  language: 'auto',
};

const loadSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('ucma_ai_settings') || '{}');
    const merged = { ...DEFAULT_SETTINGS, ...saved };
    // Migration/safety: a stale "mock" provider (saved before a real key was
    // configured) must NOT override the server's real provider. Treat it as
    // "use server default" by clearing it.
    if (merged.provider === 'mock') merged.provider = '';
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

const formatTime = (date) => {
  try {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

function AIChat({ user, onClose, isMobile }) {
  const userId = user?.id || user?._id;

  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [settings, setSettings] = useState(loadSettings);
  const [showSettings, setShowSettings] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [attachments, setAttachments] = useState([]);
  const [listening, setListening] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [activeMsgMenu, setActiveMsgMenu] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('ucma_ai_dark') === '1'
  );

  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const recognitionRef = useRef(null);

  // --- Load config + last conversation on mount ---
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    (async () => {
      // The provider/model are chosen entirely on the server (.env). The client
      // sends empty provider/model so the server default (e.g. Groq) is always
      // used — there is no provider/model picker in the UI.
      try {
        const convos = await aiApi.listConversations(userId);
        if (cancelled) return;
        if (convos.length > 0) {
          const latest = convos[0];
          setConversationId(latest._id);
          const history = await aiApi.getHistory(userId, latest._id);
          if (!cancelled) setMessages(history);
        }
      } catch {
        /* start fresh */
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // --- Auto scroll ---
  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    scrollToBottom(false);
  }, [messages.length, scrollToBottom]);

  // Auto-grow the textarea.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  // Persist settings + dark mode.
  useEffect(() => {
    localStorage.setItem('ucma_ai_settings', JSON.stringify(settings));
  }, [settings]);
  useEffect(() => {
    localStorage.setItem('ucma_ai_dark', darkMode ? '1' : '0');
  }, [darkMode]);

  // Close menus when clicking outside.
  useEffect(() => {
    const handler = () => {
      setShowMenu(false);
      setActiveMsgMenu(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // --- Sending a message ---
  const handleSend = useCallback(
    async (overrideText) => {
      const promptText = (overrideText ?? input).trim();
      if ((!promptText && attachments.length === 0) || isStreaming || !userId) return;

      const now = new Date().toISOString();
      const replyNote = replyTo
        ? `> ${(replyTo.content || '').slice(0, 200)}\n\n`
        : '';
      const fullPrompt = `${replyNote}${promptText}`.trim();

      // Encode attachments as data URLs so the backend can run OCR / document analysis.
      let encodedAttachments = [];
      try {
        encodedAttachments = await Promise.all(
          attachments.map(async (a) => {
            const base = {
              type: a.type,
              name: a.name,
              mimeType: a.mimeType,
              size: a.size,
            };
            if (a.data) return { ...base, data: a.data };
            if (a.file) {
              const data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error(`Failed to read ${a.name}`));
                reader.readAsDataURL(a.file);
              });
              return { ...base, data };
            }
            return base;
          })
        );
      } catch (err) {
        console.error('[AIChat] attachment encode failed:', err);
        encodedAttachments = attachments.map((a) => ({
          type: a.type,
          name: a.name,
          mimeType: a.mimeType,
          size: a.size,
        }));
      }

      // Optimistic user bubble (keep local preview URLs for the UI).
      const tempUser = {
        _id: `temp-user-${Date.now()}`,
        role: 'user',
        content: fullPrompt || (attachments.some((a) => a.type === 'image') ? '[Image]' : '[Attachment]'),
        attachments: [...attachments],
        createdAt: now,
        status: 'sent',
      };
      // Placeholder assistant bubble that we stream into.
      const tempAssistantId = `temp-ai-${Date.now()}`;
      const tempAssistant = {
        _id: tempAssistantId,
        role: 'assistant',
        content: '',
        createdAt: now,
        streaming: true,
      };

      setMessages((prev) => [...prev, tempUser, tempAssistant]);
      setInput('');
      setAttachments([]);
      setReplyTo(null);
      setIsStreaming(true);
      setThinking(true);

      abortRef.current = new AbortController();

      // Only send provider/model when the user explicitly chose them; empty
      // values must fall through to the server's automatic intent router.
      const outgoingSettings = { ...settings };
      if (!outgoingSettings.provider) delete outgoingSettings.provider;
      if (!outgoingSettings.model) delete outgoingSettings.model;

      await aiApi.streamChat({
        userId,
        conversationId,
        prompt: fullPrompt || ' ',
        settings: outgoingSettings,
        attachments: encodedAttachments,
        signal: abortRef.current.signal,
        onToken: (chunk) => {
          setThinking(false);
          setMessages((prev) =>
            prev.map((m) =>
              m._id === tempAssistantId ? { ...m, content: m.content + chunk } : m
            )
          );
        },
        onDone: (data) => {
          setConversationId(data.conversationId);
          setMessages((prev) =>
            prev.map((m) =>
              m._id === tempAssistantId
                ? {
                    ...m,
                    _id: data.messageId || m._id,
                    streaming: false,
                    model: data.model,
                    provider: data.provider,
                    intent: data.intent,
                  }
                : m
            )
          );
          setIsStreaming(false);
          setThinking(false);
        },
        onError: (err) => {
          setMessages((prev) =>
            prev.map((m) =>
              m._id === tempAssistantId
                ? {
                    ...m,
                    streaming: false,
                    error: !err.aborted,
                    content:
                      m.content ||
                      (err.aborted
                        ? '_Generation stopped._'
                        : `⚠️ ${err.error || 'Something went wrong. Please try again.'}`),
                  }
                : m
            )
          );
          if (err.conversationId) setConversationId(err.conversationId);
          setIsStreaming(false);
          setThinking(false);
        },
      });
    },
    [input, isStreaming, userId, conversationId, settings, attachments, replyTo]
  );

  const handleStop = () => {
    abortRef.current?.abort();
    setIsStreaming(false);
    setThinking(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // --- New chat / clear / export ---
  const handleNewChat = async () => {
    handleStop();
    setMessages([]);
    setConversationId(null);
    setShowMenu(false);
    try {
      const conv = await aiApi.createConversation(userId);
      setConversationId(conv._id);
    } catch {
      /* a new conversation is created lazily on first send anyway */
    }
  };

  const handleClearMemory = async () => {
    if (!window.confirm('Clear this AI conversation? This cannot be undone.')) return;
    setShowMenu(false);
    try {
      await aiApi.clearMemory(userId, conversationId);
    } catch {
      /* ignore */
    }
    setMessages([]);
  };

  const handleExport = () => {
    if (!conversationId) return;
    setShowMenu(false);
    window.open(aiApi.exportUrl(userId, conversationId, 'md'), '_blank');
  };

  // --- Message actions ---
  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
    } catch {
      /* ignore */
    }
    setActiveMsgMenu(null);
  };

  const shareText = async (text) => {
    setActiveMsgMenu(null);
    if (navigator.share) {
      try {
        await navigator.share({ text: text || '', title: 'UCMA AI' });
        return;
      } catch {
        /* fall through to copy */
      }
    }
    copyText(text);
  };

  const handleDeleteForMe = async (msg) => {
    setActiveMsgMenu(null);
    setMessages((prev) => prev.filter((m) => m._id !== msg._id));
    if (!String(msg._id).startsWith('temp')) {
      try {
        await aiApi.deleteMessage(userId, msg._id);
      } catch {
        /* ignore */
      }
    }
  };

  const handleStar = async (msg) => {
    setActiveMsgMenu(null);
    setMessages((prev) =>
      prev.map((m) => (m._id === msg._id ? { ...m, starred: !m.starred } : m))
    );
    if (!String(msg._id).startsWith('temp')) {
      try {
        await aiApi.starMessage(userId, msg._id);
      } catch {
        /* ignore */
      }
    }
  };

  const handleReply = (msg) => {
    setReplyTo(msg);
    setActiveMsgMenu(null);
    textareaRef.current?.focus();
  };

  // --- Voice input (Web Speech API) ---
  const toggleVoice = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Voice input is not supported in this browser.');
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput((prev) => `${prev}${transcript}`);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  // --- Attachments (file / image / paste / drag&drop) ---
  const addFiles = (files) => {
    const list = Array.from(files || []).slice(0, 5);
    const mapped = list.map((f) => ({
      type: f.type.startsWith('image/') ? 'image' : 'file',
      name: f.name || `pasted-${Date.now()}.png`,
      mimeType: f.type || 'application/octet-stream',
      size: f.size,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
      // Keep the File so we can base64-encode it on send for OCR / document AI.
      file: f,
    }));
    setAttachments((prev) => [...prev, ...mapped].slice(0, 5));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) addFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeAttachment = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  // --- Search filtering ---
  const visibleMessages = searchTerm
    ? messages.filter((m) =>
        (m.content || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : messages;

  const isEmpty = !loadingHistory && messages.length === 0;

  return (
    <div
      className={`ai-chat ${isMobile ? 'ai-chat--mobile' : ''} ${darkMode ? 'ai-dark' : ''} ${
        dragOver ? 'ai-drag' : ''
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* ---------- Header ---------- */}
      <div className="ai-header">
        {isMobile && (
          <button className="ai-icon-btn ai-back" onClick={onClose} aria-label="Back">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <div className="ai-avatar" aria-hidden>
          <img src="/ucma_ai_avatar.png" alt="" className="ai-avatar-img" />
        </div>
        <div className="ai-header-info">
          <div className="ai-header-name">UCMA AI</div>
          <div className="ai-header-status">
            {thinking || isStreaming ? (
              <span className="ai-typing-text">UCMA AI is typing…</span>
            ) : (
              <span className="ai-online-dot-wrap">
                <span className="ai-online-dot" /> Always online
              </span>
            )}
          </div>
        </div>
        <div className="ai-header-actions">
          <button
            className="ai-icon-btn"
            onClick={() => setSearchOpen((s) => !s)}
            aria-label="Search messages"
            title="Search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button>
          <div className="ai-menu-wrap" onClick={(e) => e.stopPropagation()}>
            <button
              className="ai-icon-btn"
              onClick={() => setShowMenu((s) => !s)}
              aria-label="Menu"
              title="More"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
            </button>
            {showMenu && (
              <div className="ai-menu">
                <button onClick={handleNewChat}>🆕 New chat</button>
                <button onClick={() => { setShowSettings(true); setShowMenu(false); }}>⚙️ Settings</button>
                <button onClick={handleExport} disabled={!conversationId}>⬇️ Export chat</button>
                <button onClick={handleClearMemory}>🧹 Clear memory</button>
              </div>
            )}
          </div>
          {!isMobile && (
            <button className="ai-icon-btn" onClick={onClose} aria-label="Close" title="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {searchOpen && (
        <div className="ai-search-bar">
          <input
            autoFocus
            type="text"
            placeholder="Search this conversation…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="ai-icon-btn"
            onClick={() => {
              setSearchOpen(false);
              setSearchTerm('');
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ---------- Messages ---------- */}
      <div className="ai-messages" ref={listRef}>
        {loadingHistory && <div className="ai-loading">Loading…</div>}

        {isEmpty && (
          <div className="ai-welcome">
            <div className="ai-welcome-avatar">
              <img src="/ucma_ai_avatar.png" alt="" className="ai-avatar-img" />
            </div>
            <h2>Hi {user?.username || 'there'}, I'm UCMA AI</h2>
            <p>Ask me anything — coding, writing, translation, ideas and more.</p>
            <div className="ai-suggestions">
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p.title}
                  className="ai-suggestion-card"
                  onClick={() => handleSend(p.text)}
                >
                  <span className="ai-suggestion-icon">{p.icon}</span>
                  <span className="ai-suggestion-title">{p.title}</span>
                  <span className="ai-suggestion-text">{p.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {visibleMessages.map((msg) => (
          <div
            key={msg._id}
            className={`ai-msg ai-msg--${msg.role} ${msg.error ? 'ai-msg--error' : ''}`}
          >
            {msg.role === 'assistant' && (
              <div className="ai-msg-avatar">
                <img src="/ucma_ai_avatar.png" alt="" className="ai-avatar-img" />
              </div>
            )}
            <div className="ai-msg-body">
              {msg.starred && <span className="ai-star-badge" title="Starred">★</span>}
              <div className="ai-bubble">
                {msg.role === 'assistant' ? (
                  <>
                    {msg.streaming && !msg.content ? (
                      <div className="ai-thinking">
                        <span className="ai-dot" />
                        <span className="ai-dot" />
                        <span className="ai-dot" />
                        <span className="ai-thinking-label">Thinking…</span>
                      </div>
                    ) : (
                      <Markdown content={msg.content} />
                    )}
                    {msg.streaming && msg.content && <span className="ai-cursor">▋</span>}
                  </>
                ) : (
                  <>
                    {msg.attachments?.length > 0 && (
                      <div className="ai-msg-attachments">
                        {msg.attachments.map((a, i) =>
                          a.previewUrl ? (
                            <img key={i} src={a.previewUrl} alt={a.name} />
                          ) : (
                            <span key={i} className="ai-attach-chip">📎 {a.name}</span>
                          )
                        )}
                      </div>
                    )}
                    <div className="ai-user-text">{msg.content}</div>
                  </>
                )}
              </div>
              <div className="ai-msg-meta">
                <span className="ai-msg-time">{formatTime(msg.createdAt)}</span>
                {msg.role === 'user' && <span className="ai-msg-status">✓✓</span>}
                {!msg.streaming && (
                  <div className="ai-msg-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="ai-msg-action-btn"
                      onClick={() =>
                        setActiveMsgMenu(activeMsgMenu === msg._id ? null : msg._id)
                      }
                      aria-label="Message options"
                    >
                      ⋯
                    </button>
                    {activeMsgMenu === msg._id && (
                      <div className="ai-msg-menu">
                        <button onClick={() => copyText(msg.content)}>Copy</button>
                        <button onClick={() => handleReply(msg)}>Reply</button>
                        <button onClick={() => handleStar(msg)}>
                          {msg.starred ? 'Unstar' : 'Star'}
                        </button>
                        <button onClick={() => shareText(msg.content)}>Share</button>
                        <button className="ai-danger" onClick={() => handleDeleteForMe(msg)}>
                          Delete for me
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* ---------- Reply preview ---------- */}
      {replyTo && (
        <div className="ai-reply-preview">
          <div className="ai-reply-text">
            <strong>Replying to {replyTo.role === 'assistant' ? 'UCMA AI' : 'yourself'}</strong>
            <span>{(replyTo.content || '').slice(0, 120)}</span>
          </div>
          <button className="ai-icon-btn" onClick={() => setReplyTo(null)}>✕</button>
        </div>
      )}

      {/* ---------- Attachment preview ---------- */}
      {attachments.length > 0 && (
        <div className="ai-attach-preview">
          {attachments.map((a, i) => (
            <div key={i} className="ai-attach-item">
              {a.previewUrl ? <img src={a.previewUrl} alt={a.name} /> : <span>📎 {a.name}</span>}
              <button onClick={() => removeAttachment(i)} aria-label="Remove">✕</button>
            </div>
          ))}
        </div>
      )}

      {/* ---------- Composer ---------- */}
      <div className="ai-composer">
        <input
          type="file"
          ref={fileInputRef}
          multiple
          accept="image/*,.pdf,.txt,.doc,.docx,.csv,.json,.xlsx,.xls,.md"
          style={{ display: 'none' }}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          className="ai-icon-btn"
          onClick={() => fileInputRef.current?.click()}
          title="Attach file or image"
          aria-label="Attach"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </button>
        <button
          className={`ai-icon-btn ${listening ? 'ai-listening' : ''}`}
          onClick={toggleVoice}
          title="Voice input"
          aria-label="Voice input"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>
        <textarea
          ref={textareaRef}
          className="ai-textarea"
          placeholder="Message UCMA AI…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={1}
        />
        {isStreaming ? (
          <button className="ai-send-btn ai-stop-btn" onClick={handleStop} title="Stop">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            className="ai-send-btn"
            onClick={() => handleSend()}
            disabled={!input.trim() && attachments.length === 0}
            title="Send"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91Z" />
            </svg>
          </button>
        )}
      </div>

      {/* ---------- Settings drawer ---------- */}
      {showSettings && (
        <div className="ai-settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="ai-settings" onClick={(e) => e.stopPropagation()}>
            <div className="ai-settings-header">
              <h3>AI Settings</h3>
              <button className="ai-icon-btn" onClick={() => setShowSettings(false)}>✕</button>
            </div>

            <label className="ai-setting">
              <span>Response length</span>
              <select
                value={settings.responseLength}
                onChange={(e) => setSettings({ ...settings, responseLength: e.target.value })}
              >
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="long">Long</option>
              </select>
            </label>

            <label className="ai-setting">
              <span>Creativity ({settings.temperature})</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={settings.temperature}
                onChange={(e) =>
                  setSettings({ ...settings, temperature: parseFloat(e.target.value) })
                }
              />
            </label>

            <label className="ai-setting">
              <span>Language</span>
              <select
                value={settings.language}
                onChange={(e) => setSettings({ ...settings, language: e.target.value })}
              >
                <option value="auto">Auto</option>
                <option value="English">English</option>
                <option value="Kannada">Kannada</option>
                <option value="Hindi">Hindi</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Arabic">Arabic</option>
                <option value="Chinese">Chinese</option>
              </select>
            </label>

            <label className="ai-setting ai-setting--row">
              <span>Dark mode</span>
              <input
                type="checkbox"
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
              />
            </label>

            <div className="ai-settings-footer">
              <button className="ai-btn-secondary" onClick={handleClearMemory}>
                Clear AI history
              </button>
              <button className="ai-btn-secondary" onClick={handleExport} disabled={!conversationId}>
                Download chat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIChat;
